import { type FormEvent, useEffect, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { RevealStoredValue } from '@/components/ui/RevealStoredValue';
import {
  type OrgSmtpSettings as OrgSmtpRow,
  type SmtpEncryption,
  revealOrgSmtpPassword,
  useOrgSmtpSettings,
  useTestOrgSmtp,
  useUpdateOrgSmtpSettings,
} from '@/lib/smtp';

/**
 * Per-org outbound mail settings. Lives on `OrganizationPage` for
 * shared orgs (and is hidden for personal orgs since they don't send
 * org-scoped emails — invitations and notifications come from shared
 * orgs only).
 *
 * The toggle between system and own credentials drives the rest of
 * the form: when "use shared mail server" is checked, the override
 * fields are collapsed because they have no effect.
 */
export function OrgSmtpSettings({ orgSlug }: { orgSlug: string }) {
  const settings = useOrgSmtpSettings(orgSlug);
  return (
    <section className="rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-5">
      <h2 className="text-lg font-semibold">Outbound mail (SMTP)</h2>
      <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
        Controls how this organization sends invitation and
        notification emails. Personal account emails like password
        reset always go through the site-wide mail server.
      </p>

      {settings.isPending && (
        <p className="mt-4 text-sm text-stone-500 dark:text-stone-400">Loading…</p>
      )}
      {settings.isError && (
        <p className="mt-4 text-sm text-red-600">
          {(settings.error as Error).message}
        </p>
      )}
      {settings.data && <OrgSmtpForm orgSlug={orgSlug} row={settings.data} />}
    </section>
  );
}

function OrgSmtpForm({ orgSlug, row }: { orgSlug: string; row: OrgSmtpRow }) {
  const update = useUpdateOrgSmtpSettings(orgSlug);
  const test = useTestOrgSmtp(orgSlug);

  const [enabled, setEnabled] = useState(row.enabled);
  const [useSystem, setUseSystem] = useState(row.use_system_credentials);
  const [host, setHost] = useState(row.host ?? '');
  const [port, setPort] = useState(row.port);
  const [encryption, setEncryption] = useState<SmtpEncryption>(row.encryption);
  const [username, setUsername] = useState(row.username ?? '');
  const [password, setPassword] = useState('');
  const [fromAddress, setFromAddress] = useState(row.from_address ?? '');
  const [fromName, setFromName] = useState(row.from_name ?? '');
  const [showSuccess, setShowSuccess] = useState(false);
  const [testTo, setTestTo] = useState('');

  useEffect(() => {
    setEnabled(row.enabled);
    setUseSystem(row.use_system_credentials);
    setHost(row.host ?? '');
    setPort(row.port);
    setEncryption(row.encryption);
    setUsername(row.username ?? '');
    setPassword('');
    setFromAddress(row.from_address ?? '');
    setFromName(row.from_name ?? '');
  }, [
    row.enabled,
    row.use_system_credentials,
    row.host,
    row.port,
    row.encryption,
    row.username,
    row.has_org_password,
    row.from_address,
    row.from_name,
  ]);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setShowSuccess(false);
    update.mutate(
      {
        enabled,
        use_system_credentials: useSystem,
        host: host.trim() || null,
        port,
        encryption,
        username: username.trim() || null,
        password: password ? password : null,
        from_address: fromAddress.trim() || null,
        from_name: fromName.trim() || null,
      },
      {
        onSuccess: () => {
          setShowSuccess(true);
          setPassword('');
        },
      },
    );
  };

  const onClearPassword = () => {
    if (!confirm('Clear the stored SMTP password?')) return;
    update.mutate({
      enabled,
      use_system_credentials: useSystem,
      host: host.trim() || null,
      port,
      encryption,
      username: username.trim() || null,
      clear_password: true,
      from_address: fromAddress.trim() || null,
      from_name: fromName.trim() || null,
    });
  };

  const onTest = () => {
    if (!testTo.trim()) return;
    test.mutate(testTo.trim());
  };

  const allowlistRevoked = !row.system_credentials_allowed;
  const sharedDisabled = !row.system_credentials_enabled;

  return (
    <form onSubmit={onSubmit} className="mt-4 space-y-4">
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        Enable outbound mail for this organization
      </label>

      <div className="rounded-md border border-stone-200 dark:border-stone-700 p-3">
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={useSystem}
            disabled={allowlistRevoked}
            onChange={(e) => setUseSystem(e.target.checked)}
            className="mt-1"
          />
          <span>
            Use the shared site-wide mail server
            {allowlistRevoked && (
              <span className="ml-2 rounded bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 text-xs text-amber-800 dark:text-amber-300">
                administrator revoked
              </span>
            )}
            {!allowlistRevoked && sharedDisabled && (
              <span className="ml-2 rounded bg-stone-100 dark:bg-stone-800 px-2 py-0.5 text-xs text-stone-600 dark:text-stone-400">
                shared mail server is currently disabled
              </span>
            )}
          </span>
        </label>
        <p className="ml-6 mt-1 text-xs text-stone-500 dark:text-stone-400">
          {allowlistRevoked
            ? 'Your administrator disabled shared mail for this org. Configure your own credentials below.'
            : 'When checked, this org sends through the shared relay configured by the site administrator.'}
        </p>
      </div>

      {/*
       * From-address + From-name always visible — they make sense
       * regardless of whether the org borrows the system relay or
       * runs its own SMTP. Saving a from_address while
       * `use_system_credentials=true` makes the resolver prefer the
       * org's address over the system's default, so multi-tenant
       * deploys can show "From: ops@acme.example" while sharing one
       * upstream mailer. Some relays (e.g. SES) reject mismatched
       * senders — the hint flags that.
       */}
      <div className="grid gap-3 md:grid-cols-2">
        <Field
          label="From address"
          hint={
            useSystem
              ? "Overrides the system relay's default From for this org's emails. Some relays reject mismatched senders."
              : 'What recipients see in the From header.'
          }
        >
          <Input
            value={fromAddress}
            onChange={(e) => setFromAddress(e.target.value)}
            placeholder="noreply@yourorg.com"
          />
        </Field>
        <Field label="From name (optional)">
          <Input
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            placeholder="Acme Inc Parts"
          />
        </Field>
      </div>

      {!useSystem && (
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Host">
            <Input
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="smtp.example.com"
            />
          </Field>
          <Field label="Port">
            <Input
              type="number"
              min={1}
              max={65535}
              value={port}
              onChange={(e) => setPort(Number(e.target.value) || 587)}
            />
          </Field>
          <Field label="Encryption">
            <select
              value={encryption}
              onChange={(e) => setEncryption(e.target.value as SmtpEncryption)}
              className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm"
            >
              <option value="starttls">STARTTLS (port 587)</option>
              <option value="ssl">SSL/TLS (port 465)</option>
              <option value="none">None — plaintext</option>
            </select>
          </Field>
          <Field label="Username (optional)">
            <Input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="off"
            />
          </Field>
          <Field
            label="Password / API key"
            hint={
              row.has_org_password
                ? 'A password is stored. Leave blank to keep it.'
                : 'Required if your relay needs auth.'
            }
          >
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={row.has_org_password ? 'saved — leave blank to keep' : ''}
              autoComplete="off"
            />
            {row.has_org_password && (
              <>
                <RevealStoredValue
                  fetcher={() => revealOrgSmtpPassword(orgSlug)}
                  onReveal={(v) => setPassword(v)}
                  onHide={() => setPassword('')}
                  label="stored password"
                />
                <button
                  type="button"
                  onClick={onClearPassword}
                  className="mt-1 text-xs text-red-600 hover:underline"
                >
                  Clear stored password
                </button>
              </>
            )}
          </Field>
        </div>
      )}

      {row.last_error && (
        <p className="rounded border border-red-300 bg-red-50 dark:bg-red-900/30 p-2 text-xs text-red-800 dark:text-red-300">
          Last error: {row.last_error}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={update.isPending}>
          {update.isPending ? 'Saving…' : 'Save settings'}
        </Button>
        {update.isError && (
          <p className="text-sm text-red-600">{(update.error as Error).message}</p>
        )}
        {showSuccess && (
          <p className="text-sm text-emerald-700 dark:text-emerald-400">Saved.</p>
        )}
      </div>

      <div className="border-t border-stone-200 dark:border-stone-800 pt-4">
        <h3 className="font-medium">Send a test email</h3>
        <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
          Sends a real message through the saved credentials. Save first
          if you've made changes.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Input
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="recipient@example.com"
            className="max-w-xs"
          />
          <Button
            type="button"
            variant="secondary"
            onClick={onTest}
            disabled={test.isPending || !testTo.trim()}
          >
            {test.isPending ? 'Sending…' : 'Send test'}
          </Button>
          {test.data?.ok === true && (
            <p className="text-sm text-emerald-700 dark:text-emerald-400">
              {test.data.detail}
              {test.data.source && (
                <span className="ml-1 text-stone-500 dark:text-stone-400">
                  (via {test.data.source} credentials)
                </span>
              )}
            </p>
          )}
          {test.data?.ok === false && (
            <p className="text-sm text-red-600">{test.data.detail}</p>
          )}
          {test.isError && (
            <p className="text-sm text-red-600">{(test.error as Error).message}</p>
          )}
        </div>
      </div>
    </form>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs font-medium text-stone-700 dark:text-stone-300">
        {label}
      </span>
      {children}
      {hint && (
        <span className="mt-1 block text-xs text-stone-500 dark:text-stone-400">
          {hint}
        </span>
      )}
    </label>
  );
}
